// ============================================
// HELP PAGE
// ============================================

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  HelpCircle,
  Book,
  MessageCircle,
  Mail,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileText,
  Users,
  Search,
} from 'lucide-react';
import { PageHeader } from '@/components';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/help/')({
  component: HelpPage,
});

function HelpPage() {
  const { t } = useTranslation('panel');
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState<string[]>([]);

  const faqItems = [
    {
      category: t('help.faq.categories.jobs'),
      questions: [
        {
          q: t('help.faq.jobs.q1'),
          a: t('help.faq.jobs.a1'),
        },
        {
          q: t('help.faq.jobs.q2'),
          a: t('help.faq.jobs.a2'),
        },
        {
          q: t('help.faq.jobs.q3'),
          a: t('help.faq.jobs.a3'),
        },
      ],
    },
    {
      category: t('help.faq.categories.applications'),
      questions: [
        {
          q: t('help.faq.applications.q1'),
          a: t('help.faq.applications.a1'),
        },
        {
          q: t('help.faq.applications.q2'),
          a: t('help.faq.applications.a2'),
        },
      ],
    },
    {
      category: t('help.faq.categories.account'),
      questions: [
        {
          q: t('help.faq.account.q1'),
          a: t('help.faq.account.a1'),
        },
        {
          q: t('help.faq.account.q2'),
          a: t('help.faq.account.a2'),
        },
      ],
    },
  ];

  const supportChannels = [
    {
      icon: Mail,
      title: t('help.support.email.title'),
      description: t('help.support.email.description'),
      action: 'destek@crewatsea.com',
      link: 'mailto:destek@crewatsea.com',
    },
    {
      icon: MessageCircle,
      title: t('help.support.liveChat.title'),
      description: t('help.support.liveChat.description'),
      action: t('help.support.liveChat.action'),
      link: '#',
    },
  ];

  const resources = [
    {
      icon: Book,
      title: t('help.resources.guide.title'),
      description: t('help.resources.guide.description'),
      link: '#',
    },
    {
      icon: FileText,
      title: t('help.resources.api.title'),
      description: t('help.resources.api.description'),
      link: '#',
    },
    {
      icon: Users,
      title: t('help.resources.webinars.title'),
      description: t('help.resources.webinars.description'),
      link: '#',
    },
  ];

  const toggleItem = (key: string) => {
    setOpenItems((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const filteredFaq = faqItems.map((category) => ({
    ...category,
    questions: category.questions.filter(
      (q) =>
        q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.a.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((category) => category.questions.length > 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('help.title')}
        description={t('help.description')}
      />

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('help.searchPlaceholder')}
          className="w-full h-12 pl-12 pr-4 text-sm border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500 focus:border-maritime-500"
        />
      </div>

      {/* Support Channels */}
      <div className="grid md:grid-cols-3 gap-4">
        {supportChannels.map((channel) => (
          <a
            key={channel.title}
            href={channel.link}
            className="bg-white rounded-maritime border border-gray-200 p-5 hover:border-maritime-300 hover:shadow-sm transition-all group"
          >
            <div className="flex items-start gap-4">
              <div className="p-2 bg-maritime-100 rounded-full text-maritime-600 group-hover:bg-maritime-600 group-hover:text-white transition-colors">
                <channel.icon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">{channel.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{channel.description}</p>
                <p className="text-sm font-medium text-maritime-600 mt-2">
                  {channel.action}
                </p>
              </div>
            </div>
          </a>
        ))}
      </div>

      {/* FAQ */}
      <div className="bg-white rounded-maritime border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-gray-400" />
          {t('help.faq.title')}
        </h2>

        {filteredFaq.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            {t('help.faq.noResults')}
          </p>
        ) : (
          <div className="space-y-6">
            {filteredFaq.map((category) => (
              <div key={category.category}>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  {category.category}
                </h3>
                <div className="space-y-2">
                  {category.questions.map((item, index) => {
                    const key = `${category.category}-${index}`;
                    const isOpen = openItems.includes(key);
                    return (
                      <div
                        key={key}
                        className="border border-gray-200 rounded-maritime overflow-hidden"
                      >
                        <button
                          onClick={() => toggleItem(key)}
                          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-medium text-gray-900">{item.q}</span>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                            <p className="text-sm text-gray-600">{item.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Resources */}
      <div className="bg-white rounded-maritime border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('help.resources.title')}
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {resources.map((resource) => (
            <a
              key={resource.title}
              href={resource.link}
              className="flex items-start gap-3 p-4 border border-gray-200 rounded-maritime hover:border-maritime-300 transition-colors group"
            >
              <resource.icon className="w-5 h-5 text-gray-400 group-hover:text-maritime-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-gray-900 group-hover:text-maritime-600 flex items-center gap-1">
                  {resource.title}
                  <ExternalLink className="w-3 h-3" />
                </h4>
                <p className="text-sm text-gray-500 mt-1">{resource.description}</p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
